import Vue from "vue";
import {Map, View} from "ol";
import TileLayer from "ol/layer/Tile";
import ImageLayer from "ol/layer/Image";
import {ImageWMS} from "ol/source";
import WMSCapabilities from "ol/format/WMSCapabilities";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import {optionsFromCapabilities} from "ol/source/WMTS";
import XYZ from "ol/source/XYZ";
import GoogleLayer from "olgm/layer/Google.js";
import OLGoogleMaps from "olgm/OLGoogleMaps.js";
import Feature from "ol/Feature";
import {Circle as CircleString, LineString, MultiLineString, Point, Polygon} from "ol/geom";
import {Circle, Fill, Icon, Stroke, Style} from "ol/style";
import {OSM, Vector as VectorSource, WMTS} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import M2wApi from "../m2w_api";
import {defaults, ScaleLine} from "ol/control";
import Draw, {createBox} from "ol/interaction/Draw";
import Overlay from "ol/Overlay";
import {getArea, getLength} from "ol/sphere";
import {unByKey} from "ol/Observable";
import {createEmpty, extend, isEmpty} from "ol/extent";
import GeoJSON from "ol/format/GeoJSON";
import {googleMaps} from "@/services/map/googleMaps";
import Attribution from "ol/control/Attribution";
import ZIndex from "@/services/zindex";
import {fromCircle} from "ol/geom/Polygon";
import {applyStyle} from "ol-mapbox-style";
import VectorTileLayer from "ol/layer/VectorTile.js";
import {defaults as defaultInteractions, MouseWheelZoom} from "ol/interaction";
import {toLonLat} from "ol/proj"; // 👈 pentru log în WGS84

export default class Basemap {
  constructor() {
    this.parser = new WMTSCapabilities();
    this.allLayers = [];
    this.googleLayers = [];
    this.activeLayer = 0;
    this.host_address = M2wApi.host_address;
    this.vectorArray = [];
  }

  async initMap(layer_id) {
    this.allLayers.push(...Vue.config.project.base_layers);
    if (layer_id && !isNaN(layer_id)) {
      this.activeLayer = this.allLayers.find(x => x.id === parseInt(layer_id, 10)).id;
    } else {
      this.activeLayer = Vue.config.project.default_base_layer;
    }

    this.googleLayers = this.allLayers.filter(layer => layer.name.includes("Google"));
    await this.setupMap(this.activeLayer, Vue.config.project.min_zoom, Vue.config.project.max_zoom);
    this.setupGlobalInteraction();
  }

  toRgba(color, transparency) {
    let opacity = Math.floor((1 - transparency / 100) * 255);
    let hex = ("00" + opacity.toString(16)).slice(-2);
    let shortHexRegex = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/;
    let match = color.match(shortHexRegex);
    if (match) {
      color = "#" + match[1] + match[1] + match[2] + match[2] + match[3] + match[3];
    }

    return color + hex;
  }

  createMarkerForCoordinates(coordinates) {
    let markerUrl = "static/uploads/markers/4735turquoise_pinpoint2.png";
    let marker = new Feature({
      geometry: new Point(coordinates)
    });
    let markerStyle = new Style({
      image: new Icon({
        anchor: [0.5, 1],
        crossOrigin: "anonymous",
        src: this.host_address + markerUrl
      })
    });
    marker.setStyle(markerStyle);
    let vectorSource = new VectorSource({
      features: [marker]
    });
    let vectorLayer = new VectorLayer({
      source: vectorSource,
      crossOrigin: "anonymous",
      updateWhileAnimating: true,
      updateWhileInteracting: true
    });
    vectorLayer.setZIndex(ZIndex.Marker);

    this.map.addLayer(vectorLayer);
    return vectorLayer;
  }

  drawLine(geom, strokeOptions = {color: "rgba(255,0,0,1)", width: 3}) {
    let line = new Feature({
      geometry: new GeoJSON().readGeometry(geom)
    });

    return this.drawLineInternal(line, strokeOptions);
  }

  drawEpsg4326Line(geom, strokeOptions = {color: "rgb(255,0,0)", width: 3}) {
    geom = geom.map(c => googleMaps.transformFromLatLng(c));

    let line = new Feature({
      geometry: new MultiLineString([geom])
    });

    return this.drawLineInternal(line, strokeOptions);
  }

  updateStyle(layer, strokeOptions) {
    layer.setStyle(
      new Style({
        stroke: new Stroke(strokeOptions)
      })
    );
  }

  drawLineInternal(line, strokeOptions) {
    let source = new VectorSource({
      features: [line],
      crossOrigin: "anonymous"
    });
    let style = new Style({
      stroke: new Stroke(strokeOptions)
    });
    let vectorLayer = new VectorLayer({
      source: source,
      style: style,
      updateWhileAnimating: true,
      updateWhileInteracting: true
    });
    vectorLayer.setZIndex(ZIndex.Drawing);
    this.map.addLayer(vectorLayer);
    return vectorLayer;
  }

  async setupMap(layer_id) {
    let layers = [];

    let scaleLine = new ScaleLine({target: "scale-bar"});
    let attributions = new Attribution({
      collapsible: false,
      collapsed: false,
      target: "imprint"
    });
    let controls = defaults({
      attribution: false,
      zoom: false // disables the + / − zoom buttons
    });
    controls.extend([scaleLine, attributions]);

    this.map = new Map({
      target: "map",
      layers: layers,
      controls: controls,
      interactions: defaultInteractions({mouseWheelZoom: false, dragPan: true}).extend([
        new MouseWheelZoom({
          duration: 250
        })
      ]),
      view: new View({
        center: Vue.config.project.center,
        zoom: 13,
        constrainResolution: true,
        minZoom: Vue.config.project.min_zoom,
        maxZoom: Vue.config.project.max_zoom
      })
    });

    // --- LOG CONFIG & VIEW + COORDONATE WGS84 ---
    // console.log("[cfg] center:", Vue.config.project.center);
    // console.log("[cfg] min_zoom:", Vue.config.project.min_zoom);
    // console.log("[cfg] max_zoom:", Vue.config.project.max_zoom);

    // const v = this.map.getView();
    // const centerProj = v.getCenter();
    // console.log("[view] projection code:", v.getProjection().getCode());
    // console.log("[view] center (proj):", centerProj);
    // if (centerProj) {
    //   const [lon, lat] = toLonLat(centerProj);
    //   console.log("[view] center (WGS84 lon,lat):", lon, lat);
    //   console.log("[view] center (WGS84 fixed 6):", lon.toFixed(6), lat.toFixed(6));
    // }
    // console.log("[view] zoom curent:", v.getZoom());
    // console.log("[view] minZoom efectiv:", v.getMinZoom());
    // console.log("[view] maxZoom efectiv:", v.getMaxZoom());

    this.map.on("moveend", () => {
      const view = this.map.getView();
      const c = view.getCenter();
      const z = view.getZoom();
      if (c) {
        const [lon, lat] = toLonLat(c);
        console.log(`[moveend] lon=${lon.toFixed(6)}, lat=${lat.toFixed(6)}, zoom=${z}`);
      }
    });

    // log coordonate la click
    this.map.on("singleclick", evt => {
      const [lon, lat] = toLonLat(evt.coordinate);
      console.log(`[click] lon=${lon.toFixed(6)}, lat=${lat.toFixed(6)}`);
    });
    // --- END LOGS ---

    if (this.googleLayers.length > 0) {
      let olGM = new OLGoogleMaps({
        map: this.map,
        watch: {
          tile: false,
          vector: false
        }
      });
      olGM.activate();
    }

    // this.setupSettingsButtons();
    await this.changeBasemap(layer_id, true);
    // await this.changeBasemap(layer_id, false);
  }

  setupSettingsButtons() {
    if (document.getElementsByClassName("ol-zoom")[0] && document.getElementById("setting-buttons")) {
      let buttonsFrame = document.getElementsByClassName("ol-zoom")[0];
      let layerButton = document.getElementById("setting-buttons");
      buttonsFrame.appendChild(layerButton);
      layerButton.style.display = "block";
    }
  }

  async changeBasemap(layer_id, override = true) {
    this.activeLayer = layer_id;
    const layer = this.allLayers.find(l => l.id === layer_id);

    let olLayer = null;
    try {
      if (layer.map_type === "osm") olLayer = this.getOsmLayer();
      else if (layer.map_type === "google") olLayer = this.getGoogleLayer(layer);
      else if (layer.map_type === "wmts") olLayer = await this.getWmtsLayer(layer);
      else if (layer.map_type === "wms") olLayer = await this.getWmsLayer(layer);
      else if (layer.map_type === "xyz") olLayer = await this.getXyzLayer(layer);
      else if (layer.map_type === "gl_style") {
        olLayer = new VectorTileLayer({declutter: true, className: "gl_style"});
        applyStyle(olLayer, layer.url);
      }

      const existingLayers = this.map.getLayers();
      if (override) existingLayers.setAt(0, olLayer);
      else existingLayers.insertAt(0, olLayer);
    } catch (e) {
      console.log(e);
    }
  }

  getOsmLayer() {
    return new TileLayer({source: new OSM({crossOrigin: "Anonymous"})});
  }

  getGoogleLayer(layer) {
    this.map.getView().setMinZoom(0);
    return new GoogleLayer({mapTypeId: layer.lyid});
  }

  // initial fara layer SF
  // async getWmtsLayer(layer) {
  //   const response = await fetch(layer.url);
  //   const text = await response.text();
  //   console.log(response, "cristina")

  //   let result = this.parser.read(text);
  //   let options = optionsFromCapabilities(result, {
  //     layer: layer.lyid ?? layer.name,
  //     // matrixSet: result.Contents.TileMatrixSet[0].Identifier
  //     matrixSet: layer.matrix,
  //   });
  //   console.log(options, "layer Wmts")
  //   options.attributions = layer.attributions;
  //   options.crossOrigin = "Anonymous";

  //   console.log('[SERVICE:WMTS options]', options);
  //   window.dispatchEvent(new CustomEvent('m2w:serviceLoaded', {
  //     detail: {
  //       type: 'wmts',
  //       url: options.urls?.[0],
  //       layer: options.layer,
  //       matrixSet: options.matrixSet,
  //       cfgId: layer.id,
  //       cfgName: layer.name
  //     }
  //   }));

  //   const wmtsLayer = new TileLayer({
  //     source: new WMTS(options),
  //   });
  //   wmtsLayer.set('isServiceLayer', true);
  //   wmtsLayer.set('serviceType', 'wmts');
  //   wmtsLayer.set('serviceLayer', options.layer);
  //   wmtsLayer.set('cfgName', layer.name);
  //   wmtsLayer.set('cfgId', layer.id);

  //   return wmtsLayer;
  //   // return new TileLayer({
  //   //   source: new WMTS(options),
  //   // });
  // }
  // actualizat cu layer SF
  async getWmtsLayer(layer) {
    const response = await fetch(layer.url);

    if (!response.ok) {
      throw new Error(`WMTS capabilities HTTP ${response.status}: ${layer.url}`);
    }

    const text = await response.text();
    const result = this.parser.read(text);

    const options = optionsFromCapabilities(result, {
      layer: layer.lyid ?? layer.name,
      matrixSet: layer.matrix
    });

    if (!options) {
      throw new Error(`Configuratie WMTS invalida: ${layer.lyid ?? layer.name}`);
    }

    options.attributions = layer.attributions;
    options.crossOrigin = "anonymous";

    // Configuratia serviciului WMTS pentru mecanismul existent.
    // Nu este automat LayerSF.
    const wmtsServiceConfig = {
      type: "wmts",
      capabilitiesUrl: layer.url,
      serviceUrl: options.urls?.[0] || null,
      layer: options.layer,
      matrixSet: options.matrixSet,
      format: options.format || "image/png",
      style: options.style || "default",
      cfgId: layer.id,
      cfgName: layer.name,
      attribution: layer.attributions || ""
    };

    // Pastram compatibilitatea cu celelalte componente.
    window.__m2wActiveMapService = wmtsServiceConfig;

    window.dispatchEvent(
      new CustomEvent("m2w:serviceLoaded", {
        detail: wmtsServiceConfig
      })
    );

    // Layerul este creat exclusiv pentru OpenLayers.
    const wmtsLayer = new TileLayer({
      source: new WMTS(options)
    });

    wmtsLayer.set("isServiceLayer", true);
    wmtsLayer.set("serviceType", "wmts");
    wmtsLayer.set("serviceLayer", options.layer);
    wmtsLayer.set("cfgName", layer.name);
    wmtsLayer.set("cfgId", layer.id);

    return wmtsLayer;
  }
  // async getWmsLayer(layer) {
  //   const response = await fetch(layer.url);
  //   const text = await response.text();
  //   const parserWMS = new WMSCapabilities();
  //   let result = parserWMS.read(text);
  //   let WMSurl = result.Service.OnlineResource;

  //   window.dispatchEvent(new CustomEvent('m2w:serviceLoaded', {
  //     detail: {
  //       type: 'wms',
  //       capabilitiesUrl: layer.url,
  //       url: WMSurl,
  //       layer: layer.lyid ?? layer.name,
  //     }
  //   }));
  //   const wmsLayer = new ImageLayer({
  //   source: new ImageWMS({
  //     url: WMSurl,
  //     params: { LAYERS: layer.lyid ?? layer.name },
  //   }),
  // });
  // wmsLayer.set('isServiceLayer', true);
  // wmsLayer.set('serviceType', 'wms');
  // wmsLayer.set('serviceLayer', layer.lyid ?? layer.name);
  // wmsLayer.set('cfgName', layer.name);
  // wmsLayer.set('cfgId', layer.id);
  // wmsLayer.set('serviceLayer', 'cached');

  // return wmsLayer;
  //   // return new ImageLayer({
  //   //   source: new ImageWMS({
  //   //     url: WMSurl,
  //   //     params: { LAYERS: layer.lyid ?? layer.name },
  //   //   }),
  //   // });
  // }

  async getWmsLayer(layer) {
    const response = await fetch(layer.url);
    const text = await response.text();
    const parserWMS = new WMSCapabilities();
    let result = parserWMS.read(text);
    let WMSurl = result.Service.OnlineResource;

    const layerName = layer.lyid ?? layer.name;

    window.dispatchEvent(
      new CustomEvent("m2w:serviceLoaded", {
        detail: {
          type: "wms",
          capabilitiesUrl: layer.url,
          url: WMSurl,
          layer: layerName,
          cfgId: layer.id,
          cfgName: layer.name
        }
      })
    );

    const wmsLayer = new ImageLayer({
      source: new ImageWMS({
        url: WMSurl,
        params: {LAYERS: layerName}
      })
    });

    wmsLayer.set("isServiceLayer", true);
    wmsLayer.set("serviceType", "wms");
    wmsLayer.set("serviceLayer", layerName);
    wmsLayer.set("cfgName", layer.name);
    wmsLayer.set("cfgId", layer.id);

    return wmsLayer;
  }

  async getXyzLayer(layer) {
    window.dispatchEvent(
      new CustomEvent("m2w:serviceLoaded", {
        detail: {type: "xyz", url: layer.url}
      })
    );
    const xyzLayer = new TileLayer({
      source: new XYZ({url: layer.url})
    });
    xyzLayer.set("isServiceLayer", true);
    xyzLayer.set("serviceType", "xyz");
    xyzLayer.set("cfgName", layer.name);
    xyzLayer.set("cfgId", layer.id);

    return xyzLayer;
    // return new TileLayer({ source: new XYZ({ url: layer.url }) });
  }

  startMeasurement(type, color, id) {
    let source = new VectorSource();
    let vector = new VectorLayer({
      source: source,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      style: new Style({
        fill: new Fill({
          color: "rgba(255, 255, 255, 0.6)"
        }),
        stroke: new Stroke({
          color: color,
          width: 2
        })
      })
    });
    vector.setZIndex(ZIndex.Drawing);
    this.map.addLayer(vector);

    let target = {
      id: id,
      type: type,
      layer: vector,
      area: 0,
      length: 0,
      radius: 0,
      drawing: null,
      tooltip: null,
      tooltipElement: null,
      onFinish: null,
      cancel: () => {
        if (target.onFinish) target.onFinish();
        target.drawing.abortDrawing();
        this.map.removeInteraction(target.drawing);
        this.map.removeOverlay(target.tooltip);
        this.map.removeLayer(target.layer);
      },
      remove: () => {
        this.map.removeOverlay(target.tooltip);
        this.map.removeLayer(target.layer);
      }
    };

    function createMeasureTooltip(map) {
      target.tooltipElement = document.createElement("div");
      target.tooltipElement.className = "ol-tooltip ol-tooltip-measure";
      target.tooltipElement.style.borderColor = color;
      target.tooltip = new Overlay({
        element: target.tooltipElement,
        offset: [0, -15],
        positioning: "bottom-center"
      });
      map.addOverlay(target.tooltip);
    }

    function addInteractionFun(map, type) {
      let geometryFunction;
      if (type === "Box") {
        type = "Circle";
        geometryFunction = createBox();
      }
      let draw = new Draw({
        source: source,
        type: type,
        geometryFunction: geometryFunction,
        style: new Style({
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.5)"
          }),
          stroke: new Stroke({
            color: color,
            lineDash: [10, 5],
            width: 1
          }),
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: "rgba(0, 0, 0, 0.7)"
            }),
            fill: new Fill({
              color: "rgba(255, 255, 255, 0.2)"
            })
          })
        })
      });

      map.addInteraction(draw);
      createMeasureTooltip(map);
      let listener;
      draw.on("drawstart", function(evt) {
        let sketch = evt.feature;
        let tooltipCoord = evt.coordinate;
        listener = sketch.getGeometry().on("change", function(evt) {
          let geom = evt.target;
          if (geom instanceof Polygon) {
            target.length = getLength(geom);
            target.area = getArea(geom);
            tooltipCoord = geom.getInteriorPoint().getCoordinates();
          } else if (geom instanceof LineString) {
            target.length = getLength(geom);
            tooltipCoord = geom.getLastCoordinate();
          } else if (geom instanceof CircleString) {
            let poly = fromCircle(geom);
            target.length = getLength(poly);
            target.radius = target.length / (2 * Math.PI);
            target.area = target.radius * target.radius * Math.PI;
            tooltipCoord = geom.getFirstCoordinate();
          }
          if (target.tooltipElement) {
            target.tooltipElement.innerHTML = target.id;
            target.tooltip.setPosition(tooltipCoord);
          }
        });
      });
      draw.on("drawabort", function() {
        unByKey(listener);
      });
      draw.on("drawend", function() {
        if (target.onFinish) target.onFinish();
        target.tooltipElement.className = "ol-tooltip ol-tooltip-static";
        target.tooltip.setOffset([0, -7]);
        target.tooltipElement = null;
        unByKey(listener);
        map.removeInteraction(draw);
      });
      return draw;
    }

    target.drawing = addInteractionFun(this.map, type);
    return target;
  }

  zoomToExtent(extents) {
    let extent = createEmpty();
    for (let ex of extents) extend(extent, ex);
    if (!isEmpty(extent)) this.map.getView().fit(extent);
  }

  drawEpsg4326Circle(waypoints, radius = 3, width = 3) {
    waypoints = waypoints.map(p => googleMaps.transformFromLatLng(p)).map(p => new Feature({geometry: new Point(p)}));

    let source = new VectorSource({
      features: waypoints
    });
    let style = new Style({
      image: new Circle({
        radius: radius,
        fill: new Fill({color: "white"}),
        stroke: new Stroke({
          color: "rgba(100,100,100,1)",
          width: width
        })
      })
    });
    let vectorLayer = new VectorLayer({
      source: source,
      style: style,
      zIndex: 2,
      crossOrigin: "anonymous",
      updateWhileAnimating: true,
      updateWhileInteracting: true
    });
    this.map.addLayer(vectorLayer);
    return vectorLayer;
  }

  setupGlobalInteraction() {
    let popup = document.getElementById("hover-popup");
    this.popupOverlay = new Overlay({
      element: popup,
      offset: [7, 7],
      stopEvent: false
    });
    this.map.addOverlay(this.popupOverlay);

    this.map.on("pointermove", event => {
      let features = [];
      this.map.forEachFeatureAtPixel(event.pixel, feature => features.push(feature), {
        layerFilter: layer => layer instanceof VectorLayer
      });

      let elem = this.popupOverlay.getElement();

      if (features.length > 0) {
        let feat = features[0];
        let subFeatures = feat.get("features");
        let message = "";
        if (subFeatures) {
          // dealing with a cluster
          if (subFeatures.length === 1) {
            message = subFeatures[0].get("poiElement").name;
          } else {
            this.basemap_cluster_ids = subFeatures
              .slice(0, subFeatures.length)
              .map(f => f.get("poiElement").id)
              .map(n => `${n}`)
              .join(",");
            message = subFeatures
              .slice(0, 3)
              .map(f => f.get("poiElement").name)
              .map(n => `- ${n}`)
              .join("<br>");

            if (subFeatures.length > 3) {
              message = message + "<br/>...";
            }
          }
        } else if (feat.poi && feat.poi.name) {
          message = feat.poi.name;
        } else {
          return;
        }
        elem.innerHTML = message;
        if (window.poiLayers.poi_cluster_ids != this.basemap_cluster_ids || (subFeatures && subFeatures.length === 1)) {
          elem.style.display = "block";
        }
        this.popupOverlay.setPosition(event.coordinate);
      } else if (features.length === 0) {
        elem.style.display = "none";
      }
    });

    // listener for zoom level change
    let currZoom = this.map.getView().getZoom();
    this.map.on("moveend", () => {
      let newZoom = this.map.getView().getZoom();
      if (currZoom != newZoom) {
        if (typeof window.poiLayers.cluster_elem !== "undefined") {
          window.poiLayers.cluster_elem.style.display = "none";
          window.poiLayers.cluster_elem.innerHTML = "";
        }
        currZoom = newZoom;
      }
    });
  }
}
export const basemap = new Basemap();
